import { Component, Input } from '@angular/core';
import {
    NavController,
    NavParams
} from 'ionic-angular';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/mergeMap';
import { ApiService } from '../../../../services/api';
import { Rodeo } from '../../../../model/rodeo';
import { RodeoEvent } from '../../../../model/rodeoEvent';
import { RodeoEventRegistration } from '../../../../model/rodeoEventRegistration';
import { ReportsService } from '../../../../services/reports.service';
import { EntryCostGenerator } from '../../../../lib/entryCostGenerator';
import { Account } from '../../../../model/account';
import { RodeoEventRun } from '../../../../model/rodeoEventRun';
import { Horse } from 'model/horse';
import { MembershipService } from '../../../../services/membership.service';

@Component({
    selector: 'page-check-in',
    templateUrl: 'report-check-in.html'

})
export class CheckInReport {
    @Input('rodeo') rodeo: Rodeo;

    selectedRodeoEvent: any = 'All';

    showContactInfo: boolean = true;
    searchChangeObservable;

    documentTitle: any;
    popoverActive: boolean;
    dataLoaded: boolean;
    printing: boolean;
    searchName: string;

    rodeoEventMap: any[];
    sidepotMap: any[];
    exhibitionMap: any[];
    membershipMap: any[];

    matchingMemberships: number[] = [];
    organizations: number[] = [];

    entryCostGenerator: EntryCostGenerator = EntryCostGenerator.instance();

    constructor(
        private nav: NavController,
        navParams: NavParams,
        public api: ApiService,
        public reportService: ReportsService,
        private membershipService: MembershipService
    ) {
        if (!this.rodeo)
            this.rodeo = navParams.get('rodeo');

        this.printing = navParams.get('printing');
        this.searchName = navParams.get('searchName');
        this.showContactInfo = navParams.get('showContactInfo');

        if (!this.selectedRodeoEvent || !this.selectedRodeoEvent.id) {
            this.selectedRodeoEvent = 'All';
        }

        if (this.printing) {
            setTimeout(() => {
                this.load();
            }, 0);
        }
    }

    load() {
        this.rodeoEventMap = [];
        this.sidepotMap = [];
        this.exhibitionMap = [];
        this.membershipMap = [];

        this.rodeo.rodeoEvents.forEach((rodeoEvent) => {
            this.rodeoEventMap[rodeoEvent.id] = rodeoEvent;

            rodeoEvent.sidepots.forEach((sidepot) => {
                this.sidepotMap[sidepot.id] = sidepot;
            })
        });

        this.organizations.push(this.rodeo.organization.id);
        this.rodeo.organization.memberships.forEach(membership => {
            this.membershipMap[membership.id] = membership;
            this.matchingMemberships.push(membership.id);

            this.membershipService.getMembershipPrices(membership.id).then((prices) => {
                if (prices && prices.length > 0 && prices[0].isActive) {
                    this.membershipMap[membership.id].price = prices[0].price;
                } else {
                    this.membershipMap[membership.id].price = 0;
                }
            })
        });

        this.rodeo.sanctionedOrganizations.forEach(organization => {
            this.organizations.push(organization.id);

            organization.memberships.forEach(membership => {
                this.membershipMap[membership.id] = membership;
                this.matchingMemberships.push(membership.id);

                this.membershipService.getMembershipPrices(membership.id).then((prices) => {
                    if (prices && prices.length > 0 && prices[0].isActive) {
                        this.membershipMap[membership.id].price = prices[0].price;
                    } else {
                        this.membershipMap[membership.id].price = 0;
                    }
                })
            });
        });

        this.rodeo.exhibitions.forEach((exhibition) => {
            this.exhibitionMap[exhibition.id] = exhibition;
        })

        EntryCostGenerator.instance().reloadEntryInfo(this.rodeo, this.searchName);


        this.updateRodeoEvent();
        this.documentTitle = this.rodeo.name + " - " + 'Entries';
        this.dataLoaded = true;
    }

    updateRodeoEvent() {
        EntryCostGenerator.instance().getCombinedRegistrations().forEach((contestantRegObject) => {
            if (this.selectedRodeoEvent == 'All' || contestantRegObject.registrations.some(x => x.rodeoEventId == this.selectedRodeoEvent.id)) 
                contestantRegObject.hasSelectedRodeoEvent = true;
            else
                contestantRegObject.hasSelectedRodeoEvent = false;

            contestantRegObject.registrations.forEach((registration) => {
                let contestantOneId = registration.teammateHeader ? registration.contestantTwoId : registration.contestantOneId;
                let contestantTwoId = registration.teammateHeader ? registration.contestantOneId : registration.contestantTwoId;
                let horseOneId = registration.teammateHeader ? registration.horseTwoId : registration.horseOneId;
                let horseTwoId = registration.teammateHeader ? registration.horseOneId : registration.horseTwoId;
    
                let allRuns = this.rodeo.rodeoEvents.filter(x => x.id == registration.rodeoEventId).flatMap(x => x.schedules).flatMap(x => x.runs).filter(x => 
                    x.contestantOneId == contestantOneId
                    && (!registration.horseOneId || horseOneId == x.horseOneId)
                    && (!contestantTwoId || contestantTwoId == x.contestantTwoId)
                    && (!registration.horseTwoId || horseTwoId == x.horseTwoId));
        
                let runs = allRuns.filter(x => x.round == 1);
                let maxRuns = 1;
                
                if (this.selectedRodeoEvent.ropingFormat == 'Draw')
                    maxRuns = this.selectedRodeoEvent.drawAmount;
                else if (this.selectedRodeoEvent.ropingFormat == 'PickAndDraw') {
                    maxRuns += this.selectedRodeoEvent.drawAmount;
                }
        
                let run = runs.filter(x => !x.used)[0];
                if (run && run != null) {
                    registration.run = new RodeoEventRun();
                    (registration as any).runCount = allRuns.filter(x => x.runIdentifier == run.runIdentifier && x.done).length;
                    Object.assign(registration.run, run);
                }
            });
        });
    }

    getMembershipIds(account: Account) {
        return [...new Set(account.memberships.filter(x => this.matchingMemberships.some(y => y == x.membership.id) && Date.parse(x.expiration) > Date.now()).map(x => x.id))];
    }
    getContestantMemberships(account: Account, returnValue?: string): any {    
        // Cant use getMembershipIds because not all memberships have an expiration date, so its an empty array
        const existingMemberships = account.memberships
            .filter(x => this.matchingMemberships.includes(x.membershipId));
        
        if (existingMemberships.length === 0) {
            return returnValue === 'names' ? [] : 0;
        };

        // NOTE - Permits: 1 per non member contestant per rodeo
        // NOTE - API StaffCreateEntries assigns rodeoId to memberships

        // Check for memberships assigned at time of entry for rodeo
        // Show the cost of the membership in the UI
        const rodeoMembership = existingMemberships.find(x => x.rodeoId === this.rodeo.id); 
        
        // If none, look for any other memberships (general or permit)
        // Assuming its paid for at a previous rodeo...
        // Show as 'Alread Paid' in UI
        const previousRodeoMembership = existingMemberships.filter(x => x.rodeoId > 0 && x.rodeoId != this.rodeo.id);

        switch (returnValue) {
            case 'names':
                return rodeoMembership ? [rodeoMembership.membership.name]
                    : previousRodeoMembership.length > 0 ? ['(Already Paid)'] : [];
            case 'ids':
                return existingMemberships.map((x) => x.membershipId);
            case 'total':
                return rodeoMembership ? this.membershipMap[rodeoMembership.membershipId].price : 0;
            default:
                return existingMemberships;
        }
    }

    getContestantMembershipNames(account: Account): string {
        const names = this.getContestantMemberships(account, 'names');

        if (names.length > 0) {
            return names.join(',');
        } else {
            return 'None';
        }
    }

    getContestantMembershipsTotal(account: Account): number {
        const total = this.getContestantMemberships(account, 'total');
        return total;
    }

    getTotalWithMembership(account: Account) {
        const currentTotal = this.entryCostGenerator.getContestantTotalFee(account.id);

        // Dont calculate with total if not TYRA
        if (this.organizations.indexOf(170) === -1) {
            return currentTotal;
        }

        const membershipTotal = this.getContestantMemberships(account, 'total');

        return currentTotal + membershipTotal;
      
    }

    onNameSearchChange(searchValue) {
        if (!this.searchChangeObservable) {
            Observable.create(observer => {
                this.searchChangeObservable = observer;
            }).debounceTime(700)
                .distinctUntilChanged()
                .subscribe((text) => {
                    this.load();
                });
        }

        this.searchChangeObservable.next(searchValue);
    }

    getBackNumber(rodeoEvent: RodeoEvent, account: Account, horse: Horse) {
        if (!rodeoEvent)
            return;

        let matchingBackNumbers = null;

        if (this.rodeo.backNumbers && this.rodeo.backNumbers.length > 0) {
            matchingBackNumbers = this.rodeo.backNumbers.filter(x => x.eventType == rodeoEvent.eventType);

            if (matchingBackNumbers.length == 0)
                matchingBackNumbers = this.rodeo.backNumbers.filter(x => !x.eventType);
        }

        var backNumber = null;
        if (matchingBackNumbers != null) {
            if (this.rodeo.backNumberConfigs[0].byHorse) {
                if (horse) {
                    backNumber = matchingBackNumbers.filter(x => x.horseId == horse.id)[0];
                }
            } else if (account)  {
                backNumber = matchingBackNumbers.filter(x => x.accountId == account.id)[0];
            }
        }

        if (backNumber != null)
            return '(' + backNumber.number + ')';
        else
            return '';
    }

    getRodeoEvent(id: number) {
        return this.rodeo.rodeoEvents.filter(x => x.id == id)[0];
    }

    getSidepotName(registration: RodeoEventRegistration, id: number) {
        var matchingSidepots = this.rodeo.rodeoEvents.flatMap(x => x.sidepots).filter(x => x.id == id);

        if (matchingSidepots.length > 0)
            return matchingSidepots[0].name;

        return "N/A";
    }

    getExhibitionName(id: number) {
        return this.rodeo.exhibitions.filter(x => x.id == id)[0].name;
    }

    matchRodeoEvents(a, b) {
        return a.id == b.id;
    }

    isRoughStock(rodeoEvent: RodeoEvent) {
        if (rodeoEvent.eventType === 'BullRiding' || rodeoEvent.eventType === 'SaddleBroncRiding' || rodeoEvent.eventType === 'RanchBroncRiding' || rodeoEvent.eventType === 'BarebackRiding')
            return true;
        else
            return false;
    }

    ionViewDidEnter() {
        setTimeout(() => {
            window.document.title = this.documentTitle;
            window.print();
            this.nav.pop();
        }, 0);
    }

    print() {
        if (this.popoverActive)
            return;

        this.popoverActive = true;

        this.nav.push(CheckInReport, {
            rodeo: this.rodeo,
            printing: true,
            searchName: this.searchName,
            showContactInfo: this.showContactInfo
        }).then(() => { this.popoverActive = false; });
    }
}


// WEBPACK FOOTER //
// ./src/pages/staff/rodeo/reports/report-check-in.ts